import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@/components/base/Badge";
import AreaChart from "@/components/feature/AreaChart";
import DonutChart from "@/components/feature/DonutChart";
import { kpis, recentOrders, orderDistribution, revenue7, revenue30, revenue90 } from "@/mocks/dashboard";
import type { RecentOrderStatus } from "@/mocks/dashboard";

const statusTone: Record<RecentOrderStatus, "amber" | "blue" | "green" | "red"> = {
  جديد: "amber",
  "قيد التنفيذ": "blue",
  مكتمل: "green",
  ملغي: "red",
};

type Period = "7" | "30" | "90";

function playChime() {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const note = (freq: number, start: number, dur: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    };
    note(880, 0, 0.18);
    note(1174.66, 0.14, 0.22);
    setTimeout(() => ctx.close(), 1000);
  } catch {
    /* الصوت اختياري */
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>("7");
  const [toast, setToast] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setToast(false), 10000);
    playChime();
    return () => clearTimeout(t);
  }, []);

  const revenueData = period === "7" ? revenue7 : period === "30" ? revenue30 : revenue90;

  return (
    <div>
      <h1 className="text-[28px] font-bold text-ghasla-text-heading">لوحة التحكم</h1>

      {/* KPI cards */}
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="group rounded-[20px] border border-[#EAF5F5] bg-white p-6 shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-glow"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#E8F7F7] to-[#D3EFEF] text-ghasla-text-link transition-transform duration-300 group-hover:scale-105">
                <i className={`${kpi.icon} text-lg`} />
              </span>
              <span className="text-sm text-ghasla-text-secondary">{kpi.label}</span>
            </div>
            <p className="mt-3 text-[32px] font-bold leading-none text-ghasla-text-heading">{kpi.value}</p>
            {kpi.direction !== "none" && (
              <p
                className={`mt-2 text-[13px] font-semibold ${
                  kpi.direction === "up" ? "text-[#27AE60]" : "text-[#E74C3C]"
                }`}
              >
                {kpi.change}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Recent orders + distribution */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-5">
        {/* Recent orders */}
        <div className="overflow-hidden rounded-[20px] border border-[#EAF5F5] bg-white shadow-card xl:col-span-3">
          <div className="flex items-center justify-between px-5 py-5">
            <h2 className="text-lg font-semibold text-ghasla-text-heading">آخر الطلبات</h2>
            <button type="button" onClick={() => navigate("/provider/orders")} className="cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline">
              عرض الكل
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="bg-[#F0FDFD] text-xs font-semibold text-ghasla-text-secondary">
                  <th className="h-12 px-5 text-right">رقم الطلب</th>
                  <th className="h-12 px-5 text-right">العميل</th>
                  <th className="h-12 px-5 text-right">الخدمة</th>
                  <th className="h-12 px-5 text-right">الحالة</th>
                  <th className="h-12 px-5 text-right">المبلغ</th>
                  <th className="h-12 px-5 text-right">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o) => (
                  <tr key={o.id} className="border-b border-[#F4F8F8] transition-colors last:border-0 hover:bg-[#FAFDFD]">
                    <td className="h-14 px-5 font-medium text-ghasla-text-link">{o.id}</td>
                    <td className="h-14 px-5 text-ghasla-text-primary">{o.customer}</td>
                    <td className="h-14 px-5 text-ghasla-text-primary">{o.service}</td>
                    <td className="h-14 px-5">
                      <Badge tone={statusTone[o.status]}>{o.status}</Badge>
                    </td>
                    <td className="h-14 px-5 font-semibold text-ghasla-text-heading">{o.amount} ر.س</td>
                    <td className="h-14 px-5">
                      <button type="button" onClick={() => navigate(`/provider/orders/${o.id}`)} className="cursor-pointer text-sm font-medium text-ghasla-text-link hover:underline">
                        عرض
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Distribution */}
        <div className="rounded-[20px] border border-[#EAF5F5] bg-white p-6 shadow-card xl:col-span-2">
          <h2 className="text-base font-semibold text-ghasla-text-heading">توزيع الطلبات حسب الخدمة</h2>
          <div className="mt-6 flex justify-center">
            <DonutChart segments={orderDistribution} centerValue="24" centerLabel="طلب اليوم" />
          </div>
          <div className="mt-6 space-y-3">
            {orderDistribution.map((d) => (
              <div key={d.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
                  <span className="text-ghasla-text-primary">{d.label}</span>
                </div>
                <span className="font-semibold text-ghasla-text-heading">{d.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue chart */}
      <div className="mt-5 rounded-[20px] border border-[#EAF5F5] bg-white p-6 shadow-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ghasla-text-heading">الإيرادات</h2>
          <div className="flex items-center gap-1 rounded-full bg-[#F0FDFD] p-1">
            {(
              [
                { key: "7", label: "7 أيام" },
                { key: "30", label: "30 يوم" },
                { key: "90", label: "3 شهور" },
              ] as { key: Period; label: string }[]
            ).map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPeriod(p.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  period === p.key ? "bg-[#0D6E6E] text-white" : "text-ghasla-text-secondary hover:text-ghasla-text-link"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-6">
          <AreaChart
            data={revenueData.map((d) => ({ label: d.label, value: d.amount }))}
            valueSuffix=" ر.س"
          />
        </div>
      </div>

      {/* New order toast */}
      {toast && (
        <div className="fixed inset-x-4 top-20 z-40 md:left-6 md:right-auto md:top-20">
          <div className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-card" style={{ borderRight: "4px solid #F59E0B" }}>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF9EB] text-[#F59E0B]">
              <i className="ri-notification-3-line text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-ghasla-text-heading">طلب جديد #GH-2853</p>
              <p className="mt-0.5 text-xs text-ghasla-text-secondary">غسيل سيارات — 70 ر.س</p>
              <button type="button" onClick={() => navigate("/provider/orders/GH-2853")} className="mt-1 cursor-pointer text-xs font-medium text-ghasla-text-link hover:underline">
                عرض
              </button>
            </div>
            <button
              type="button"
              onClick={() => setToast(false)}
              className="ml-1 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#F0FDFD]"
              aria-label="إغلاق"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}